import React, { useState, useEffect, useRef } from "react";
import { ChevronDown, X, Filter } from "lucide-react";
import axios from "axios";
import { useStore } from "@nanostores/react";
import { $products, $filter } from "./products-store";
import { FilterOption, FilterData } from "./types";

interface FilterState {
  [key: string]: string[];
}

const FilterComponentHorizontal: React.FC = () => {
  const [filterData, setFilterData] = useState<FilterData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedFilters, setSelectedFilters] = useState<FilterState>({});
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  console.log("filter horizontal render");

  // Read filters from URL
  const getFiltersFromURL = (): FilterState => {
    if (typeof window === "undefined") return {};
    const params = new URLSearchParams(window.location.search);
    const filters: FilterState = {};

    params.getAll("filter").forEach(param => {
      const [type, option] = param.split(":");
      if (type && option) {
        if (!filters[type]) filters[type] = [];
        filters[type].push(option);
      }
    });

    return filters;
  };

  // Update URL with filters
  const updateURLWithFilters = (filters: FilterState) => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);

    // Remove all existing filter params
    params.delete("filter");

    // Add current filters
    Object.entries(filters).forEach(([type, options]) => {
      options.forEach(option => {
        params.append("filter", `${type}:${option}`);
      });
    });

    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;

    window.history.pushState({}, "", newUrl);
  };

  // Fetch filter options
  const fetchFilterOptions = async (): Promise<FilterData> => {
    const result = await axios.get("/sfapi/products-legacy/filter");
    const tags = result.data.map(d => d._id) || [];
    const filterOptions = tags.reduce((acc: FilterOption[], tag: string) => {
      const [type, option] = tag.split(":");
      // Only "Type:Option" style tags are filters; skip plain tags like "grandeffects"
      if (!type || !option) return acc;
      if (!acc.some(item => item.type === type)) {
        acc.push({ type, options: [] });
      }
      const filterOption = acc.find(item => item.type === type);
      if (filterOption) {
        filterOption.options.push(option);
      }
      return acc;
    }, []);

    // Sort filter options alphabetically within each type
    filterOptions.forEach(filterOption => {
      filterOption.options.sort((a, b) => a.localeCompare(b));
    });

    // Priority order for filter types
    const priorityOrder = [
      "Type",
      "Brand",
      "Campaign/Program/Channels",
      "Product Category"
    ];

    // Sort filter types with priority types first, then alphabetically
    filterOptions.sort((a, b) => {
      const aIndex = priorityOrder.indexOf(a.type);
      const bIndex = priorityOrder.indexOf(b.type);

      // If both are in priority list, sort by priority order
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }

      // If only a is in priority list, it comes first
      if (aIndex !== -1) {
        return -1;
      }

      // If only b is in priority list, it comes first
      if (bIndex !== -1) {
        return 1;
      }

      // If neither is in priority list, sort alphabetically
      return a.type.localeCompare(b.type);
    });

    return { filterOptions };
  };

  // Handle data reload
  const handleDataReload = (filters: FilterState) => {
    console.log("Reloading data with filters:", filters);
  };

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const data = await fetchFilterOptions();
        setFilterData(data);

        // Load filters from URL
        const urlFilters = getFiltersFromURL();
        setSelectedFilters(urlFilters);

        // Convert to tags for the store
        const tags = Object.entries(urlFilters).reduce(
          (acc: string[], [filterType, options]) => {
            return acc.concat(options.map(option => `${filterType}:${option}`));
          },
          []
        );
        $filter.set(tags);
      } catch (error) {
        console.error("Failed to load filter options:", error);
      } finally {
        setLoading(false);
      }
    };

    loadFilters();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdown) {
        const ref = dropdownRefs.current[openDropdown];
        if (ref && !ref.contains(event.target as Node)) {
          setOpenDropdown(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdown]);

  const toggleDropdown = (filterType: string) => {
    setOpenDropdown(openDropdown === filterType ? null : filterType);
  };

  const handleFilterChange = (
    filterType: string,
    option: string,
    checked: boolean
  ) => {
    setSelectedFilters(prev => {
      const currentFilters = prev[filterType] || [];
      const newFilters = checked
        ? [...currentFilters, option]
        : currentFilters.filter(item => item !== option);

      const updatedFilters = {
        ...prev,
        [filterType]: newFilters
      };

      // Remove empty filter types
      if (newFilters.length === 0) {
        delete updatedFilters[filterType];
      }

      handleDataReload(updatedFilters);

      const tags = Object.entries(updatedFilters).reduce(
        (acc: string[], [filterType, options]) => {
          return acc.concat(options.map(option => `${filterType}:${option}`));
        },
        []
      );
      $filter.set(tags);

      // Update URL with new filters
      updateURLWithFilters(updatedFilters);

      return updatedFilters;
    });
  };

  const removeFilter = (filterType: string, option: string) => {
    handleFilterChange(filterType, option, false);
  };

  const clearAllFilters = () => {
    setSelectedFilters({});
    handleDataReload({});
    $filter.set([]);
    updateURLWithFilters({});
  };

  const getActiveFiltersCount = () => {
    return Object.values(selectedFilters).reduce(
      (acc, val) => acc + val.length,
      0
    );
  };

  const getFilterTypeCount = (filterType: string) => {
    return (
      (selectedFilters[filterType] && selectedFilters[filterType].length) || 0
    );
  };

  if (loading) {
    return (
      <div className="bg-white border-b border-gray-200 py-3">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse flex gap-2">
            <div className="h-9 bg-gray-200 rounded-md w-24"></div>
            <div className="h-9 bg-gray-200 rounded-md w-24"></div>
            <div className="h-9 bg-gray-200 rounded-md w-24"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
      <div className="mx-auto max-w-7xl px-4 py-3 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-3">
          {/* Filter Dropdowns Row */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 text-xs sm:text-sm font-medium text-gray-700">
              <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Filters:</span>
            </div>

            {/* Filter Type Dropdowns */}
            {filterData &&
              filterData.filterOptions.map(filterOption => {
                const count = getFilterTypeCount(filterOption.type);
                return (
                  <div
                    key={filterOption.type}
                    className="relative"
                    ref={el => (dropdownRefs.current[filterOption.type] = el)}
                  >
                    <button
                      onClick={() => toggleDropdown(filterOption.type)}
                      className={`inline-flex items-center gap-1.5 sm:gap-2 rounded-md px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium shadow-sm transition-colors ${
                        count > 0
                          ? "bg-primary text-white hover:bg-primary-700"
                          : "bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      <span className="whitespace-nowrap">
                        {filterOption.type}
                      </span>
                      {count > 0 && (
                        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] sm:min-w-[20px] sm:h-5 px-1 text-[10px] sm:text-xs font-semibold text-primary bg-white rounded-full">
                          {count}
                        </span>
                      )}
                      <ChevronDown
                        className={`w-3 h-3 sm:w-4 sm:h-4 transition-transform ${
                          openDropdown === filterOption.type ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    {/* Dropdown Menu */}
                    {openDropdown === filterOption.type && (
                      <div className="absolute left-0 sm:left-auto right-0 sm:right-auto mt-2 w-[calc(100vw-2rem)] sm:w-72 max-w-md rounded-lg bg-white shadow-xl ring-1 ring-black ring-opacity-5 z-20 max-h-[300px] sm:max-h-[280px] overflow-hidden flex flex-col">
                        <div className="overflow-y-auto flex-1 p-3 sm:p-4 overscroll-contain">
                          <div className="space-y-2 sm:space-y-3">
                            {filterOption.options.map(option => {
                              const isChecked =
                                (selectedFilters[filterOption.type] &&
                                  selectedFilters[filterOption.type].includes(
                                    option
                                  )) ||
                                false;
                              return (
                                <label
                                  key={option}
                                  className="flex items-start text-sm cursor-pointer hover:bg-gray-50 -mx-1 px-2 py-2 rounded transition-colors"
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={e =>
                                      handleFilterChange(
                                        filterOption.type,
                                        option,
                                        e.target.checked
                                      )
                                    }
                                    className="mt-0.5 w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary focus:ring-2 flex-shrink-0"
                                  />
                                  <span className="ml-3 text-gray-700 break-words leading-snug">
                                    {option}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

            {/* Clear All Button */}
            {getActiveFiltersCount() > 0 && (
              <button
                onClick={clearAllFilters}
                className="ml-auto text-xs sm:text-sm font-medium text-primary hover:text-primary-700 underline whitespace-nowrap"
              >
                Clear All ({getActiveFiltersCount()})
              </button>
            )}
          </div>

          {/* Active Filters Pills Row */}
          {getActiveFiltersCount() > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 pb-1">
              <span className="text-[10px] sm:text-xs font-medium text-gray-500 whitespace-nowrap">
                Active:
              </span>
              {Object.entries(selectedFilters).map(([filterType, options]) =>
                options.map(option => (
                  <span
                    key={`${filterType}-${option}`}
                    className="inline-flex items-center gap-1 px-2 py-1 sm:px-3 sm:py-1.5 bg-primary/10 text-primary text-xs sm:text-sm font-medium rounded-full ring-1 ring-inset ring-primary/20"
                  >
                    <span className="text-[10px] sm:text-xs font-normal text-gray-600">
                      {filterType}:
                    </span>
                    <span className="truncate max-w-[120px] sm:max-w-none">
                      {option}
                    </span>
                    <button
                      onClick={() => removeFilter(filterType, option)}
                      className="hover:bg-primary/20 rounded-full p-0.5 transition-colors flex-shrink-0"
                      aria-label={`Remove ${option} filter`}
                    >
                      <X className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                    </button>
                  </span>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilterComponentHorizontal;
